import {
  ID,
  newTaskSuffix,
  overdueSectionID,
  rootId,
  SortingMode,
  TaskListItemTypes,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getSortedTasks } from '../get-sorted-tasks';
import { UpcomingDeadlineBuckets } from './build-upcoming-deadline-buckets';

export type BuildUpcomingDeadlineTreeOptions = {
  buckets: UpcomingDeadlineBuckets;
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  hideNewTaskRow?: boolean;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildUpcomingDeadlineTree = ({
  buckets,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  hideNewTaskRow,
  rootLabel,
  newTaskLabel,
}: BuildUpcomingDeadlineTreeOptions): Record<ID, TaskListTreeItem> => {
  const { sections, sectionTasks } = buckets;

  const treeItems: Record<ID, TaskListTreeItem> = {};
  const sectionIds: ID[] = [];

  sections.forEach((section) => {
    const tasksInSection = sectionTasks[section.id];

    // Overdue is a virtual derived section — omit it when nothing is overdue.
    if (section.id === overdueSectionID && !tasksInSection.length) {
      return;
    }

    // Habits appear in multiple day sections; per-section custom order is
    // necessary because a flattened order's `indexOf` would let the earliest
    // section override every later one.
    const sectionCustomOrder =
      sortingMode === SortingMode.Custom ? customOrder?.[section.id] ?? [] : [];

    const sortedTasks = getSortedTasks(tasksInSection, {
      today,
      weekStartsOn,
      order: sectionCustomOrder,
      mode:
        section.id === overdueSectionID
          ? [SortingMode.DueDate]
          : sortingMode
            ? [SortingMode.DueDate, sortingMode]
            : undefined,
      allDayFirst: true,
      invertedTimeslots: section.id === overdueSectionID,
    });

    treeItems[section.id] = {
      id: section.id,
      type: TaskListItemTypes.Date,
      name: section.id,
      children: [],
      isFolder: true,
    };
    sectionIds.push(section.id);

    sortedTasks.forEach((task) => {
      treeItems[section.id].children!.push(task.id);
      treeItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };
    });

    if (!hideNewTaskRow && section.isDay && section.id !== overdueSectionID) {
      const newTaskItemId = `${section.id}${newTaskSuffix}`;
      treeItems[section.id].children!.push(newTaskItemId);
      treeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    }
  });

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: sectionIds,
    },
    ...treeItems,
  };
};
